import React, { useState } from 'react';
import '../../App.css';
import '../../styles/lobby.css';
import { AppHeader, PackHeaderTitle, BgAnimate } from '../shared';
import { BurgerMenu } from '../menu/Menu';
import MenuPageWrapper from '../menu/MenuPageWrapper';
import HowToPlayPage from '../menu/HowToPlayPage';
import PremiumPage from '../menu/PremiumPage';
import HelpPage from '../menu/HelpPage';
import { appVariant } from '../../config';
import { getPackHeader } from '../../config/packHeader';
import { getMenuGameLinks } from '../../config/gameLinks';
import { useTranslation } from '../../i18n';
import { getGameLinkName } from '../../i18n/helpers';

type MenuPage = 'how-to-play' | 'premium' | 'support' | null;

interface RoomSetupProps {
  onHost: () => void;
  onJoin: () => void;
  onPremiumJoinRoom?: () => void;
  onPhoneOnlyPlay: () => void;
}

const RoomSetup: React.FC<RoomSetupProps> = ({ onHost, onJoin, onPremiumJoinRoom, onPhoneOnlyPlay }) => {
  const [currentMenuPage, setCurrentMenuPage] = useState<MenuPage>(null);
  const { t } = useTranslation();

  const getGameType = (): 'blitz' | 'team' | 'family' | 'party' => {
    const variantMap: Record<string, 'blitz' | 'team' | 'family' | 'party'> = {
      blitz: 'blitz',
      team: 'team',
      family: 'family',
      party: 'party',
      new: 'party',
      coop: 'blitz',
    };
    return variantMap[appVariant] || 'blitz';
  };

  const { name: packName, phrase: packPhrase } = getPackHeader(appVariant);
  const gameLinks = getMenuGameLinks(getGameType());

  const menuItems = [
    {
      id: 'play-another-game-header',
      label: t('lobby.menu.playAnotherGamePack'),
      isSectionHeader: true,
    },
    ...gameLinks.map((link) => ({
      id: link.id,
      label: getGameLinkName(link.id),
      href: link.url,
    })),
    {
      id: 'menu-divider',
      isDivider: true,
    },
    {
      id: 'menu-section-header',
      label: t('lobby.menu.sectionHeader'),
      isSectionHeader: true,
    },
    {
      id: 'how-to-play',
      label: t('common.howToPlay'),
      onClick: () => setCurrentMenuPage('how-to-play'),
    },
    {
      id: 'premium',
      label: t('common.premium'),
      onClick: () => setCurrentMenuPage('premium'),
    },
    {
      id: 'support',
      label: t('common.support'),
      onClick: () => setCurrentMenuPage('support'),
    },
  ];

  if (currentMenuPage === 'how-to-play') {
    return (
      <MenuPageWrapper
        title={t('common.howToPlay')}
        onMenuClick={setCurrentMenuPage}
        onBack={() => setCurrentMenuPage(null)}
        gameType={getGameType()}
      >
        <HowToPlayPage />
      </MenuPageWrapper>
    );
  }

  if (currentMenuPage === 'premium') {
    return (
      <MenuPageWrapper
        title={t('common.premium')}
        onMenuClick={setCurrentMenuPage}
        onBack={() => setCurrentMenuPage(null)}
        gameType={getGameType()}
      >
        <PremiumPage gameType={getGameType()} />
      </MenuPageWrapper>
    );
  }

  if (currentMenuPage === 'support') {
    return (
      <MenuPageWrapper
        title={t('common.support')}
        onMenuClick={setCurrentMenuPage}
        onBack={() => setCurrentMenuPage(null)}
        gameType={getGameType()}
      >
        <HelpPage />
      </MenuPageWrapper>
    );
  }

  return (
    <div className="app room-setup-app" style={{ position: 'relative' }} data-testid="room-setup-page">
      <BgAnimate speed={2.0} delayBetweenCycles={2000} />
      <div style={{ position: 'relative', zIndex: 1 }}>
        <AppHeader
          rightButton={undefined}
          centerContent={<PackHeaderTitle name={packName} phrase={packPhrase} />}
          rightContent={<BurgerMenu items={menuItems} />}
        />
        <div className="container room-setup-container" data-testid="room-setup-container">
          <div className="room-setup-hub" data-testid="room-setup-grid">
            <div className="room-setup-hub__actions">
              <button
                type="button"
                className="room-setup-hub__btn room-setup-hub__btn--host"
                onClick={onHost}
                data-testid="room-setup-tile-primary"
              >
                {t('lobby.roomSetup.hostGame')}
              </button>
              <p className="room-setup-hub__hint">{t('lobby.roomSetup.hostHint')}</p>

              <button
                type="button"
                className="room-setup-hub__btn room-setup-hub__btn--join"
                onClick={onJoin}
                data-testid="room-setup-tile-secondary"
              >
                {t('lobby.roomSetup.joinWithCode')}
              </button>
              <p className="room-setup-hub__hint">{t('lobby.roomSetup.joinHint')}</p>
            </div>

            <div className="room-setup-hub__links">
              <button type="button" className="room-setup-hub__link" onClick={onPhoneOnlyPlay}>
                {t('lobby.roomSetup.playOnPhonesOnly')}
              </button>
              <span className="room-setup-hub__link-sep" aria-hidden="true">
                ·
              </span>
              <button type="button" className="room-setup-hub__link" onClick={() => setCurrentMenuPage('premium')}>
                {t('lobby.roomSetup.getPremium')}
              </button>
              {onPremiumJoinRoom && (
                <>
                  <span className="room-setup-hub__link-sep" aria-hidden="true">
                    ·
                  </span>
                  <button type="button" className="room-setup-hub__link" onClick={onPremiumJoinRoom}>
                    {t('lobby.roomSetup.haveCode')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomSetup;
